import Image from 'next/image';
import React, { memo } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { Group, LocationOn, MonetizationOn } from '@mui/icons-material';
import { useI18nContext } from '@procore/core-react';
import { Building, Toolbox, WrenchHammer } from '@procore/core-icons';
import { uniq } from 'lodash';
import {
  buildCompanySizeText,
  buildAvgContractSizeText,
  buildJoinedProcoreInText,
} from '../../helpers/build-texts';
import { Business } from '../../types/business';
import ImageLoader from '../utils/ImageLoader';
import BusinessLogo, { LogoSize } from './BusinessLogo';
import About from './About';
import { useStyles } from './BusinessProfileInfo.styles';
import BusinessMetadata, { ProfileInfoView } from './BusinessMetadata';
import { sortCoverageAreas } from '../CoverageAreas/AreaOfCoverage';
import EmailObfuscationWrapper from '../utils/EmailObfuscationWrapper';

interface BusinessProfileInfoProps {
  view: ProfileInfoView;
  business: Business;
  fullAbout?: boolean;
  logoSize: LogoSize;
}

const ProcoreIcon = () => (
  <Image
    loader={ImageLoader}
    src="/procore-gray-icon.svg"
    alt="Procore gray icon"
    width={21}
    height={16}
    loading="eager"
    style={{
      marginLeft: '1px',
      maxWidth: '100%',
      height: 'auto',
    }}
  />
);

const BusinessProfileInfo = memo(
  ({
    view,
    business,
    fullAbout = true,
    logoSize,
  }: BusinessProfileInfoProps) => {
    const I18n = useI18nContext();
    const classes = useStyles();
    const companySizeText = business.companySize
      ? buildCompanySizeText(business, I18n)
      : '';
    const avgContractSizeText = business.avgContractSize
      ? buildAvgContractSizeText(business, I18n)
      : '';
    const joinedProcoredInText = business.monolithCreatedAt
      ? buildJoinedProcoreInText(business, I18n)
      : '';

    return (
      <Card style={{ height: '100%' }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <Box
            display="flex"
            flexDirection="row"
            className={classes.profileUpperSection}
          >
            <Box className={classes.companyLogo} flexShrink="0">
              <BusinessLogo logoUrl={business.logoUrl} size={logoSize} />
            </Box>
            <Box ml={2} className={classes.profileInfo}>
              <Box>
                <Typography
                  variant="h2"
                  style={{
                    display: 'inline-block',
                    marginTop: '-4px',
                    marginRight: '10px',
                    fontSize: '2.8rem',
                    lineHeight: '3.2rem',
                  }}
                  data-test-id="business-name"
                >
                  <EmailObfuscationWrapper>
                    {business.display.name}
                  </EmailObfuscationWrapper>
                </Typography>
              </Box>
              <Box mt={1} width="100%">
                <BusinessMetadata
                  data-test-id="coverage-areas"
                  view={view}
                  Icon={LocationOn}
                  items={uniq(sortCoverageAreas(business.coverageAreas || []))}
                  separator=" • "
                  visibleOn={['serp']}
                />
                <BusinessMetadata
                  data-test-id="business-types"
                  view={view}
                  Icon={Toolbox}
                  items={business.businessTypes}
                />
                <BusinessMetadata
                  data-test-id="construction-sectors"
                  view={view}
                  Icon={Building}
                  items={business.constructionSectors.sort((a, b) =>
                    a.localeCompare(b)
                  )}
                />
                <BusinessMetadata
                  data-test-id="provided-services"
                  view={view}
                  Icon={WrenchHammer}
                  items={business.providedServices
                    .map((item) => item.name)
                    .sort((a, b) => a.localeCompare(b))}
                />
                <BusinessMetadata
                  data-test-id="company-size"
                  view={view}
                  Icon={Group}
                  items={
                    companySizeText
                      ? [
                          `${I18n.t(
                            'components.company_size.label'
                          )}: ${companySizeText}`,
                        ]
                      : []
                  }
                  visibleOn={['profile']}
                />
                <BusinessMetadata
                  data-test-id="avg-contract-size"
                  view={view}
                  Icon={MonetizationOn}
                  items={
                    avgContractSizeText
                      ? [
                          `${I18n.t(
                            'components.avg_contract_size.label'
                          )}: ${avgContractSizeText}`,
                        ]
                      : []
                  }
                  visibleOn={['profile']}
                />
                <BusinessMetadata
                  data-test-id="joined-procore-in"
                  view={view}
                  Icon={ProcoreIcon}
                  items={joinedProcoredInText ? [joinedProcoredInText] : []}
                  visibleOn={['profile']}
                />
              </Box>
            </Box>
          </Box>
          <About business={business} fullAbout={fullAbout} view={view} />
        </Box>
      </Card>
    );
  }
);

export default BusinessProfileInfo;
